import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { RevealRequest, RevealResponse } from '@lp/shared';
import { api } from './api.js';
import { useToast } from '../components/Toast.js';

export type RevealVars = {
  targetType: RevealRequest['targetType'];
  targetId: string;
  /** Required by the server for either reference solution. */
  confirm?: boolean;
};

/**
 * Progressive disclosure, server-side.
 *
 * The reveal row is the record that the learner chose to see this, so the page
 * must not hold the result in component state — a reload would re-hide
 * something they already opened. Revealing therefore invalidates the payload
 * the block came from and lets the refetch bring the body back with
 * `revealed: true` on it.
 */
export function useReveal(
  queryKey: readonly unknown[],
): UseMutationResult<RevealResponse, unknown, RevealVars> {
  const queryClient = useQueryClient();
  const { toastError } = useToast();

  return useMutation({
    mutationFn: (vars: RevealVars) =>
      api.post<RevealResponse>('/progress/reveal', {
        targetType: vars.targetType,
        targetId: vars.targetId,
        confirm: vars.confirm ?? false,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => {
      toastError(error, 'That could not be opened just now.');
    },
  });
}
